import * as React from "react";
import {
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  ColumnDef,
} from "@tanstack/react-table";
import { Input } from "components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/table";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import Loading from "components/loading";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "components/ui/tooltip";
import { Button } from "components/ui/button";
import { Trash2 } from "lucide-react";
import PageHeader from "components/page-header";
import { Card } from "components/ui/card";
import {
  createTeamMember,
  deleteTeamMember,
  getTeamList,
  Team,
} from "action/misc-action";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "components/ui/dialog";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useUserData } from "store/user-data";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { useSessionControl } from "hooks/use-session-control";

const schema = z.object({
  username: z.string().min(1, "Username is required"),
  email: z.string().email("Invalid email address"),
  phone_number: z.string().min(1, "Phone number is required"),
  password: z.string().min(6, "Password must be at least 6 characters long"),
  role: z.string().min(1, "Role is required"),
});

const StorboltTeamListComponent = () => {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});
  const [data, setData] = React.useState<Team[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showList, setShowList] = useState(true);
  const [isAddUserDialogOpen, setIsAddUserDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedTeamMember, setSelectedTeamMember] = useState<Team | null>(
    null,
  );
  const [isBusinessOwner, setIsBusinessOwner] = useState(false);
  const { sessionControl } = useSessionControl();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    mode: "all",
    defaultValues: {
      username: "",
      email: "",
      phone_number: "",
      password: "",
      role: "staff",
    },
  });

  useEffect(() => {
    const userData = useUserData.getState().userData;
    if (userData) {
      setIsBusinessOwner(userData.is_business_owner);
    }

    setIsLoading(true);
    getTeamList()
      .then((response) => {
        if (response.status === 200) {
          setData(response.body);
          setIsLoading(false);
        } else {
          sessionControl(response.status);

          toast.error(response.error);
          setIsLoading(false);
        }
      })
      .catch((err: any) => {
        toast.error("Failed to get Team Members, an error occurred");
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns: ColumnDef<Team>[] = [
    {
      accessorKey: "username",
      header: "Username",
      cell: ({ row }) => <span>{row.getValue("username")}</span>,
    },
    {
      accessorKey: "email",
      header: "Email",
      cell: ({ row }) => {
        const email = row.getValue("email") as string;
        return <span>{email.toLowerCase()}</span>;
      },
    },
    {
      accessorKey: "phone_number",
      header: "Phone Number",
      cell: ({ row }) => <span>{row.getValue("phone_number")}</span>,
    },
    {
      id: "actions",
      header: "Actions",
      enableHiding: false,
      cell: ({ row }) => {
        const teamMember = row.original;
        const userData = useUserData.getState().userData;
        return (
          <div className="flex items-center gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="w-7 h-7"
                    disabled={userData?.username === teamMember.username}
                    onClick={() => {
                      // Open delete confirmation dialog
                      setSelectedTeamMember(teamMember);
                      setIsDeleteDialogOpen(true);
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>Delete</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  const onSubmit = (data: z.infer<typeof schema>) => {
    const toastId = toast.loading("Creating team member...");
    createTeamMember(data).then((response) => {
      if (response.status === 201) {
        toast.success("Team member created successfully", { id: toastId });
      } else {
        if (response.status === 401) {
          toast.dismiss(toastId);
          sessionControl(response.status);
          return;
        }
        toast.error(response.error, { id: toastId });
      }

      setIsAddUserDialogOpen(false);
      reset();
    });
  };

  const handleDelete = () => {
    // Implement deletion logic here
    if (selectedTeamMember) {
      const toastId = toast.loading("Deleting team member...");
      deleteTeamMember(selectedTeamMember.id).then((response) => {
        if (response.status === 200) {
          toast.success("Team member deleted successfully", { id: toastId });
          setData(data.filter((member) => member.id !== selectedTeamMember.id));
        } else {
          if (response.status === 401) {
            toast.dismiss(toastId);
            sessionControl(response.status);
            return;
          }
          toast.error(response.error, { id: toastId });
        }
      });
    }
    setIsDeleteDialogOpen(false);
    setSelectedTeamMember(null);
  };

  return (
    <>
      {showList ? (
        <div className="w-full">
          <PageHeader
            title="Team"
            actions={
              isBusinessOwner && (
                <Button onClick={() => setIsAddUserDialogOpen(true)}>
                  Add User
                </Button>
              )
            }
          />

          <Card className="overflow-hidden">
          <div className="border-b p-4 md:px-5">
            <div className="w-full md:max-w-xs">
              <Input
                placeholder="Search..."
                value={
                  (table.getColumn("username")?.getFilterValue() as string) ??
                  ""
                }
                onChange={(event) =>
                  table
                    .getColumn("username")
                    ?.setFilterValue(event.target.value)
                }
                size="md"
              />
            </div>
          </div>

          {/* Phones: one card per team member. */}
          <div className="divide-y md:hidden">
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => {
                const teamMember = row.original;
                const isYou = useUserData.getState().userData?.username === teamMember.username;
                return (
                  <div key={row.id} className="flex items-start gap-3 p-4">
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium text-default-900">{teamMember.username}</div>
                      {teamMember.email && (
                        <div className="mt-0.5 truncate text-sm text-default-600">{teamMember.email.toLowerCase()}</div>
                      )}
                      {teamMember.phone_number && (
                        <div className="text-sm text-default-600">{teamMember.phone_number}</div>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      color="secondary"
                      size="icon"
                      className="h-9 w-9 flex-none"
                      aria-label="Delete"
                      disabled={isYou}
                      onClick={() => {
                        setSelectedTeamMember(teamMember);
                        setIsDeleteDialogOpen(true);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                );
              })
            ) : isLoading ? (
              <div className="p-6">
                <Loading />
              </div>
            ) : (
              <div className="p-10 text-center text-sm text-default-600">It's just you.</div>
            )}
          </div>

          <div className="hidden md:block">
          <Table>
            <TableHeader className="bg-default-50 [&_tr]:border-t-0">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className="h-[75px]">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : isLoading ? (
                <TableRow>
                  <TableCell className="h-[75px]" colSpan={columns.length}>
                    <Loading />
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    It's just you.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          </div>
          </Card>

          {/* Add User Dialog */}
          <Dialog
            open={isAddUserDialogOpen}
            onOpenChange={setIsAddUserDialogOpen}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Team Member</DialogTitle>
                <DialogDescription>
                  Please fill in the details to add a new team member.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <label
                    htmlFor="username"
                    className="block text-sm font-medium text-default-700"
                  >
                    Username
                  </label>
                  <Input id="username" {...register("username")} required />
                  {errors.username && (
                    <p className="text-destructive text-sm">
                      {errors.username.message}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-default-700"
                  >
                    Email
                  </label>
                  <Input
                    id="email"
                    type="email"
                    {...register("email")}
                    required
                  />
                  {errors.email && (
                    <p className="text-destructive text-sm">
                      {errors.email.message}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="phone_number"
                    className="block text-sm font-medium text-default-700"
                  >
                    Phone Number
                  </label>
                  <Input
                    id="phone_number"
                    {...register("phone_number")}
                    required
                  />
                  {errors.phone_number && (
                    <p className="text-destructive text-sm">
                      {errors.phone_number.message}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-default-700"
                  >
                    Password
                  </label>
                  <Input
                    id="password"
                    type="password"
                    {...register("password")}
                    required
                  />
                  {errors.password && (
                    <p className="text-destructive text-sm">
                      {errors.password.message}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="role"
                    className="block text-sm font-medium text-default-700"
                  >
                    Role
                  </label>
                  <Select>
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Select Role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>Role</SelectLabel>
                        <SelectItem
                          value="manager"
                          onClick={() => {
                            setValue("role", "manager");
                          }}
                        >
                          Manager - Elevated Permissions
                        </SelectItem>
                        <SelectItem
                          value="staff"
                          onClick={() => {
                            setValue("role", "staff");
                          }}
                        >
                          Staff
                        </SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  {errors.role && (
                    <p className="text-destructive text-sm">
                      {errors.role.message}
                    </p>
                  )}
                </div>
                <div className="flex justify-end space-x-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsAddUserDialogOpen(false);
                      reset();
                    }}
                    type="button"
                  >
                    Cancel
                  </Button>
                  <Button variant="default" type="submit">
                    Add User
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog
            open={isDeleteDialogOpen}
            onOpenChange={setIsDeleteDialogOpen}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete Team Member</DialogTitle>
                <DialogDescription>
                  Are you sure you want to delete{" "}
                  <strong>{selectedTeamMember?.username}</strong>? This action
                  cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setIsDeleteDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  className="bg-destructive"
                  onClick={handleDelete}
                >
                  Delete
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      ) : null}
    </>
  );
};

export default StorboltTeamListComponent;
